import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/Layout/AdminLayout';
import { Calendar, Users, Building, Bell, TrendingUp, Clock } from 'lucide-react';
import { eventsAPI, bookingsAPI, usersAPI } from '../services/api';
import { getHijriDate, getGregorianDate } from '../utils/dateUtils';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalEvents: 0,
    upcomingEvents: 0,
    totalBookings: 0,
    pendingBookings: 0,
    totalUsers: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [currentDate] = useState(new Date());

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [eventsRes, bookingsRes, usersRes] = await Promise.all([
          eventsAPI.getAll(),
          bookingsAPI.getAll(),
          usersAPI.getAll()
        ]);

        const events = eventsRes.data;
        const bookings = bookingsRes.data;
        const users = usersRes.data;

        const now = new Date();
        const upcomingEvents = events.filter(event => new Date(event.startDate) > now);
        const pendingBookings = bookings.filter(booking => booking.status === 'pending');

        setStats({
          totalEvents: events.length,
          upcomingEvents: upcomingEvents.length,
          totalBookings: bookings.length,
          pendingBookings: pendingBookings.length,
          totalUsers: users.length,
          recentActivity: [
            ...events.slice(0, 3).map(event => ({
              id: event.id,
              type: 'event',
              title: event.title,
              date: event.createdAt,
              description: 'تم إنشاء حدث جديد'
            })),
            ...bookings.slice(0, 2).map(booking => ({
              id: booking.id,
              type: 'booking',
              title: `حجز قاعة - ${booking.purpose}`,
              date: booking.createdAt,
              description: 'طلب حجز جديد'
            }))
          ].sort((a, b) => new Date(b.date) - new Date(a.date))
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'إجمالي الأحداث',
      value: stats.totalEvents,
      icon: Calendar,
      color: 'bg-blue-500',
      change: `+${stats.upcomingEvents} قادم`,
      path: '/events'
    },
    {
      title: 'الحجوزات',
      value: stats.totalBookings,
      icon: Building,
      color: 'bg-green-500',
      change: `${stats.pendingBookings} في الانتظار`,
      path: '/bookings'
    },
    {
      title: 'المستخدمون',
      value: stats.totalUsers,
      icon: Users,
      color: 'bg-purple-500',
      change: 'نشط',
      path: '/users'
    },
    {
      title: 'الإشعارات',
      value: '12',
      icon: Bell,
      color: 'bg-accent-gold',
      change: 'تم الإرسال اليوم',
      path: '/notifications'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-text-primary">لوحة التحكم الرئيسية</h1>
          <p className="text-text-secondary">مرحباً بك في نظام إدارة مسجد الحنان والمنان</p>
          <div className="mt-3 space-y-1">
            <p className="text-sm text-text-muted">
              <span className="font-medium">التاريخ الهجري:</span> {getHijriDate(currentDate)}
            </p>
            <p className="text-sm text-text-muted">
              <span className="font-medium">التاريخ الميلادي:</span> {getGregorianDate(currentDate)}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <div
              key={index}
              onClick={() => navigate(stat.path)}
              className="bg-card rounded-xl p-6 border border-divider cursor-pointer hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-text-secondary text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-text-primary mt-2">{stat.value}</p>
                  <p className="text-text-muted text-xs mt-1">{stat.change}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Activity & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-text-primary">النشاطات الأخيرة</h3>
              <Clock className="h-5 w-5 text-text-muted" />
            </div>
            
            <div className="space-y-4">
              {stats.recentActivity.map((activity) => (
                <div key={`${activity.type}-${activity.id}`} className="flex items-start space-x-4 space-x-reverse">
                  <div className={`p-2 rounded-lg ${
                    activity.type === 'event' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                  }`}>
                    {activity.type === 'event' ? 
                      <Calendar className="h-4 w-4" /> : 
                      <Building className="h-4 w-4" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary">{activity.title}</p>
                    <p className="text-xs text-text-muted">{activity.description}</p>
                    <p className="text-xs text-text-muted mt-1">
                      {new Date(activity.date).toLocaleDateString('ar-SA')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-text-primary">إجراءات سريعة</h3>
              <TrendingUp className="h-5 w-5 text-text-muted" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/events')}
                className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg text-center transition-colors group"
              >
                <Calendar className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-blue-800">حدث جديد</p>
              </button>

              <button
                onClick={() => navigate('/halls')}
                className="p-4 bg-green-50 hover:bg-green-100 rounded-lg text-center transition-colors group"
              >
                <Building className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-green-800">قاعة جديدة</p>
              </button>

              <button
                onClick={() => navigate('/users')}
                className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg text-center transition-colors group"
              >
                <Users className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-purple-800">عرض المستخدمين</p>
              </button>

              <button
                onClick={() => navigate('/notifications')}
                className="p-4 bg-yellow-50 hover:bg-yellow-100 rounded-lg text-center transition-colors group"
              >
                <Bell className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-yellow-800">إرسال إشعار</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;